import { useState } from 'react';
import { IonSegment, IonSegmentButton, IonLabel, IonList, IonText } from '@ionic/react';
import { useQuery } from '@apollo/client/react';
import { GET_CHANNEL_DRAFTS } from '../../../../graphql/queries/channelDrafts';
import type {
  GetChannelDraftsQuery,
  GetChannelDraftsQueryVariables,
} from '../../../../types/graphql-generated';
import { ChannelPostList } from './ChannelPostList';
import { DraftListItem } from './DraftListItem';

type Segment = 'published' | 'drafts';

interface ChannelPostsPanelProps {
  channelId: string;
}

// Owner-facing view of a channel's posts. A segmented control always lets the
// owner switch between the public feed ("Publicaciones") and their private
// "Borradores"; the Borradores tab shows a count when there are drafts and an
// empty state otherwise. The segment stays visible even with zero drafts so the
// feature never appears to vanish after publishing the last draft. The feed
// stays mounted (toggled with `hidden`) so its scroll and pagination survive
// switching segments.
export const ChannelPostsPanel = ({ channelId }: ChannelPostsPanelProps) => {
  const { data } = useQuery<GetChannelDraftsQuery, GetChannelDraftsQueryVariables>(
    GET_CHANNEL_DRAFTS,
    { variables: { channelId }, fetchPolicy: 'cache-and-network' },
  );

  const drafts = data?.channelDrafts ?? [];
  const [segment, setSegment] = useState<Segment>('published');

  return (
    <>
      <IonSegment
        className="mb-3"
        value={segment}
        onIonChange={(e) => setSegment((e.detail.value as Segment) || 'published')}
      >
        <IonSegmentButton value="published">
          <IonLabel>Publicaciones</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="drafts">
          <IonLabel>{drafts.length > 0 ? `Borradores · ${drafts.length}` : 'Borradores'}</IonLabel>
        </IonSegmentButton>
      </IonSegment>

      <div hidden={segment !== 'published'}>
        <ChannelPostList channelId={channelId} />
      </div>
      <div hidden={segment !== 'drafts'}>
        {drafts.length === 0 ? (
          <div className="flex justify-center py-8">
            <IonText color="medium">
              <p>Aún no tienes borradores</p>
            </IonText>
          </div>
        ) : (
          <IonList>
            {drafts.map((draft) => (
              <DraftListItem key={draft.id} channelId={channelId} draft={draft} />
            ))}
          </IonList>
        )}
      </div>
    </>
  );
};
